/**
 * NewsModal Component
 * 
 * Modal component for creating and editing news items
 * Features:
 * - Form/Preview tabs
 * - File upload functionality
 * - HTML help for description formatting
 * - File management (remove existing files)
 * 
 * @component
 */
import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import WysiwygEditor from '../Common/WysiwygEditor';

const NewsModal = ({
    show,
    onHide,
    editingNews,
    newsFormData,
    onFormChange,
    onSubmit,
    onReset,
    newsStatuses,
    getFileIcon,
    onDeleteFile
}) => {
    // Modal state
    const [newsFormTab, setNewsFormTab] = useState('form');

    if (!show) return null;

    const baseUrl = process.env.REACT_APP_API_URL || '/api';

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {editingNews ? 'Edit News' : 'Add New News'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>

                    <div className="modal-body">
                        {/* Tab Navigation */}
                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${newsFormTab === 'form' ? 'active' : ''}`}
                                    onClick={() => setNewsFormTab('form')}
                                >
                                    <i className="bi bi-pencil me-1"></i>
                                    Form
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${newsFormTab === 'preview' ? 'active' : ''}`}
                                    onClick={() => setNewsFormTab('preview')}
                                >
                                    <i className="bi bi-eye me-1"></i>
                                    Preview
                                </button>
                            </li>
                        </ul>

                        {/* Tab Content */}
                        {newsFormTab === 'form' ? (
                            <form onSubmit={onSubmit}>
                                <div className="row">
                                    <div className="col-md-8 mb-3">
                                        <label htmlFor="news-title" className="form-label">Title <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="news-title"
                                            name="title"
                                            value={newsFormData.title}
                                            onChange={onFormChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="news-status" className="form-label">Status <span className="text-danger">*</span></label>
                                        <select
                                            className="form-select"
                                            id="news-status"
                                            name="status"
                                            value={newsFormData.status}
                                            onChange={onFormChange}
                                            required
                                        >
                                            {newsStatuses.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="news-content" className="form-label">Content <span className="text-danger">*</span></label>
                                    <WysiwygEditor
                                        value={newsFormData.content}
                                        onChange={onFormChange}
                                        name="content"
                                        id="news-content"
                                        placeholder="Enter news content..."
                                        height="300px"
                                    />
                                    <div className="form-text">Rich text editor with HTML formatting support.</div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="news-files" className="form-label">Upload Files</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="news-files"
                                        name="files"
                                        multiple
                                        onChange={onFormChange}
                                        accept=".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                                    />
                                    <div className="form-text">
                                        Accepted formats: PDF, DOC, DOCX, TXT, PPT, PPTX, XLS, XLSX, JPG, PNG, GIF
                                    </div>
                                </div>

                                {/* Display existing files */}
                                {editingNews && editingNews.files && editingNews.files.length > 0 && (
                                    <Card className="mb-3">
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <i className="bi bi-files me-2"></i>
                                                Existing Files
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="row">
                                                {editingNews.files.map((file, index) => (
                                                    <div key={index} className="col-md-12 mb-2">
                                                        <div className="d-flex justify-content-between align-items-center border p-3 rounded">
                                                            <div className="d-flex align-items-center flex-grow-1">
                                                                <i className={`${getFileIcon(file.mimetype)} me-2`}></i>
                                                                <div className="flex-grow-1">
                                                                    <span className="fw-medium" title={file.originalName}>
                                                                        {file.originalName}
                                                                    </span>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {(file.size / 1024).toFixed(1)} KB
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div className="btn-group btn-group-sm">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => {
                                                                        const url = `${baseUrl}/news/files/${file.filename}`;
                                                                        window.open(url, '_blank');
                                                                    }}
                                                                    title="Download file"
                                                                >
                                                                    <i className="bi bi-download"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary"
                                                                    onClick={() => {
                                                                        const url = `${baseUrl}/news/files/${file.filename}`;
                                                                        navigator.clipboard.writeText(url).then(() => {
                                                                            alert('File URL copied to clipboard!');
                                                                        }).catch(() => {
                                                                            alert('Failed to copy URL');
                                                                        });
                                                                    }}
                                                                    title="Copy file URL"
                                                                >
                                                                    <i className="bi bi-link-45deg"></i>
                                                                </button>
                                                                {onDeleteFile && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger"
                                                                        onClick={() => onDeleteFile(editingNews._id, file.filename)}
                                                                        title="Delete file"
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </form>
                        ) : (
                            /* Preview Tab */
                            <div>
                                <h4>News Preview</h4>
                                
                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <strong>Title:</strong> {newsFormData.title || 'No title'}
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Status:</strong> 
                                        <span className="badge bg-primary ms-2">{newsFormData.status}</span>
                                    </div>
                                </div>

                                {newsFormData.content && (
                                    <div className="mb-3">
                                        <strong>Content:</strong>
                                        <div className="border p-3 mt-1" dangerouslySetInnerHTML={{ __html: newsFormData.content }} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" onClick={onSubmit}>
                            {editingNews ? 'Update News' : 'Create News'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsModal;
